import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'wouter';
import { FileText, Bell, Share2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePreferences } from '@/hooks/usePreferences';
import { track } from '@/lib/analytics';

interface Screen {
  Icon: LucideIcon;
  title: string;
  sub: string;
}

const SCREENS: Screen[] = [
  {
    Icon: FileText,
    title: 'Import your timeline',
    sub: "Paste or upload any format — bullet list, table, PDF, or photo.",
  },
  {
    Icon: Bell,
    title: 'Get notified, not distracted',
    sub: "Alerts at 10 and 5 minutes before each event so you're always prepared.",
  },
  {
    Icon: Share2,
    title: 'Share with your team',
    sub: 'Give vendors a read-only link to your live timeline.',
  },
];

export function OnboardingPage() {
  const [screen, setScreen] = useState(0);
  const [direction, setDirection] = useState(1);
  const { updatePreference } = usePreferences();
  const [, navigate] = useLocation();

  const goTo = (next: number) => {
    setDirection(next > screen ? 1 : -1);
    setScreen(next);
  };

  const finish = async (skipped: boolean) => {
    track('onboarding_completed', { skipped });
    await updatePreference({ onboardingComplete: true });
    sessionStorage.setItem('wedtrack:show_permission_modal', '1');
    navigate('/schedule');
  };

  const currentScreen = SCREENS[screen];

  return (
    <div
      className="flex flex-col min-h-screen relative overflow-hidden"
      style={{ background: 'var(--color-background)' }}
    >
      {/* Skip button — screens 0 and 1 */}
      {screen < 2 && (
        <button
          onClick={() => void finish(true)}
          className="absolute top-4 right-4 z-10 text-sm px-3 py-2 cursor-pointer"
          style={{
            color: 'var(--color-text-muted)',
            background: 'transparent',
            border: 'none',
          }}
        >
          Skip
        </button>
      )}

      {/* Screen content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pb-24">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={screen}
            custom={direction}
            initial={{ x: direction * 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -direction * 300, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="flex flex-col items-center text-center w-full"
          >
            {/* Icon */}
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mb-10"
              style={{ background: 'rgba(212,168,75,0.15)' }}
            >
              <currentScreen.Icon size={44} style={{ color: 'var(--color-primary)' }} />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-text)' }}>
              {currentScreen.title}
            </h1>

            {/* Subtitle */}
            <p
              className="text-[15px] leading-relaxed max-w-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {currentScreen.sub}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Pagination dots */}
        <div className="flex gap-2 mt-12">
          {SCREENS.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{
                background:
                  i === screen ? 'var(--color-primary)' : 'rgba(255,255,255,0.3)',
                transform: i === screen ? 'scale(1.2)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-8 left-0 right-0 px-6 max-w-lg mx-auto">
        {screen < 2 ? (
          <div className="flex justify-end">
            <button
              onClick={() => goTo(screen + 1)}
              className="text-base font-semibold px-4 py-2 cursor-pointer"
              style={{
                color: 'var(--color-primary)',
                background: 'transparent',
                border: 'none',
              }}
            >
              Next →
            </button>
          </div>
        ) : (
          <button
            onClick={() => void finish(false)}
            className="w-full h-12 rounded-xl font-semibold text-base cursor-pointer"
            style={{ background: 'var(--color-primary)', color: '#1A1A2E', border: 'none' }}
          >
            Get Started
          </button>
        )}
      </div>
    </div>
  );
}
